import { createHashedObject } from 'crono-fe-common/utils/object';
import { IScoreSettingsItemConfig } from 'crono-fe-common/components/LeadScore/lead-score.types';
import { PatchScoringSettings } from 'crono-fe-common/types/DTO/patchScoringSettings';
import { SentimentAnalysisResult } from 'crono-fe-common/types/enums/sentimentAnalysisResult';
import SentimentAnalysisBadge from 'crono-fe-common/components/SentimentAnalysisBadge';
import useGetSignalPreferences from 'hooks/services/signal/useGetSignalPreferences';
import SignalType from 'crono-fe-common/types/enums/signalType';
import { useAuth } from 'context/auth';

export const useLeadScoreSignalsConfig = () => {
  const { data: signalPreferencesDb } = useGetSignalPreferences();
  const signalPreferences = signalPreferencesDb?.data.data;
  const { user } = useAuth();
  const hasSignals = Boolean(user?.otherSettings?.hasSignals);

  const scoreSettingsSignalsConfig: IScoreSettingsItemConfig<
    keyof PatchScoringSettings['signals']
  >[] = [
    {
      enabledKey: 'hasEmailOpensSignalEnabled',
      weightKey: 'emailOpensSignalWeight',
      scoreDetailKey: 'EmailOpens',
      signalKey: SignalType.INTERESTED_OPEN,
      getLabel: () => 'Email opens',
      getDetailLabel: (value) => `Email was opened ${value} times`,
      getTooltip: (type) => {
        if (type === 'Account') {
          return 'When a contact from this company receives a signal on "Email Open" this adds to the score';
        }

        return `When the ${
          type === 'Lead' ? 'Lead' : 'Contact'
        } receives a signal on "Email Open" this adds to the score`;
      },
      footer: (
        <div className="text-xs text-gray-700">
          Score increases after ({signalPreferences?.numberOfEmailOpens}) email
          opens
        </div>
      ),
    },
    {
      enabledKey: 'hasEmailClickSignalEnabled',
      weightKey: 'emailClickSignalWeight',
      scoreDetailKey: 'EmailClicks',
      signalKey: SignalType.INTERESTED_CLICK,
      getLabel: () => 'Email Link Clicks',
      getDetailLabel: (value) => `Email was clicked ${value} times`,
      getTooltip: (type) => {
        if (type === 'Account') {
          return 'When a contact from this company receives a signal on "Email Link Click" this adds to the score';
        }

        return `When the ${
          type === 'Lead' ? 'Lead' : 'Contact'
        } receives a signal on "Email Link Click" this adds to the score`;
      },
      footer: (
        <div className="text-xs text-gray-700">
          Score increases after ({signalPreferences?.numberOfLinkClicks}) email
          link clicks
        </div>
      ),
    },
    {
      enabledKey: 'hasWebsiteVisitsSignalEnabled',
      weightKey: 'websiteVisitsSignalWeight',
      scoreDetailKey: 'WebsiteVisits',
      signalKey: SignalType.WEBSITE_VISIT,
      getLabel: () => 'Website visits',
      getDetailLabel: () => 'Website was visited',
      getTooltip: (type) =>
        `When the ${
          type === 'Lead' ? 'Lead' : 'Contact'
        } receives a signal on "Website Visit" this adds to the score`,
      disabled: !signalPreferences?.hasSnitcher,
      footer: (
        <div className="text-xs text-gray-700">
          A signal is created when a company inside or outside Crono views your
          website.
        </div>
      ),
    },
    {
      enabledKey: 'hasNeutralRepliesSignalEnabled',
      weightKey: 'neutralRepliesSignalWeight',
      scoreDetailKey: 'NeutralReplies',
      labelSuffix: (
        <SentimentAnalysisBadge
          sentimentAnalysisResult={SentimentAnalysisResult.NEUTRAL}
        />
      ),
      getDetailLabel: () => 'Replied neutrally',
      getLabel: () => (
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-800">Neutral Replies</span>

          <SentimentAnalysisBadge
            sentimentAnalysisResult={SentimentAnalysisResult.NEUTRAL}
          />
        </div>
      ),
      getTooltip: () =>
        'If the reply is positive, the score becomes 100. If it’s negative, the score becomes 0. If it’s neutral, the score is adjusted based on your preferred weighting.',
      footer: (
        <div className="flex flex-col gap-0.5 text-sm text-gray-700">
          <div className="flex items-center gap-2">
            <SentimentAnalysisBadge
              sentimentAnalysisResult={SentimentAnalysisResult.NEGATIVE}
            />

            <span>Reply sets to 0</span>
          </div>

          <div className="flex items-center gap-2">
            <SentimentAnalysisBadge
              sentimentAnalysisResult={SentimentAnalysisResult.POSITIVE}
            />

            <span>Reply sets to 100</span>
          </div>
        </div>
      ),
    },
    {
      enabledKey: 'hasRoleChangeSignalEnabled',
      weightKey: 'roleChangeSignalWeight',
      scoreDetailKey: 'RoleChange',
      signalKey: SignalType.ROLE_CHANGE,
      getLabel: () => 'Promotions',
      getDetailLabel: () => 'Promoted to a new role',
      getTooltip: (type) => {
        if (type === 'Account') {
          return 'When a contact from this company changes "Role" this will add to the score.';
        }

        return `When the ${
          type === 'Lead' ? 'Lead' : 'Contact'
        } changes "Role" this will add to the score.`;
      },
    },
    {
      enabledKey: 'hasCompanyChangeSignalEnabled',
      weightKey: 'companyChangeSignalWeight',
      scoreDetailKey: 'CompanyChange',
      signalKey: SignalType.COMPANY_CHANGE,
      getLabel: () => 'Company change',
      getDetailLabel: () => 'Changed company',
      getTooltip: (type) => {
        return `When the ${
          type === 'Lead' ? 'Lead' : 'Contact'
        } changes "Company" this will add to the score.`;
      },
      types: ['Prospect', 'Lead'],
    },
    {
      enabledKey: 'hasHiringSignalEnabled' as const,
      weightKey: 'hiringSignalWeight' as const,
      scoreDetailKey: 'Hiring' as const,
      signalKey: SignalType.HIRING_COMPANY,
      getLabel: () => 'Hiring companies',
      getDetailLabel: () => 'Company is hiring',
      getTooltip: () =>
        'When a company is hiring for a relevant role this will add to the score.',
      types: ['Account'] as PatchScoringSettings['type'][],
      disabled: !hasSignals,
    },
  ];

  const getHashedSettingsSignalsConfig = () => {
    return createHashedObject(scoreSettingsSignalsConfig, 'scoreDetailKey');
  };

  return {
    scoreSettingsSignalsConfig,
    getHashedSettingsSignalsConfig,
  };
};
